import type { AuthScope } from '../claim-keys';

// Per-user work scope as a LIST (brief §4.18) — `auth:PermissionCatalog@v1`.
//
// A user's scope is one or more `{kind: REGION|COUNTRY|AIRPORT, code}` entries, or the
// single `{kind: ALL}` (the tenant's whole operating set). It replaces the single
// `AuthScope` object of auth:X-Operator-Context@v2. Effective airports = union of
// `expandScope` over the entries ∩ the tenant's operating set — see
// `expandScopes` in @nexa/airport-reference (kept there so this file stays catalog-free).
//
// FAIL CLOSED (brief §3 defect 1, §4.18): an unknown or malformed shape means NO airports,
// never "unrestricted". Only an explicit ALL entry is unrestricted (within the tenant).

export type ScopeEntry =
  | { kind: 'REGION' | 'COUNTRY' | 'AIRPORT'; code: string }
  | { kind: 'ALL' };

const CODED_KINDS: ReadonlySet<string> = new Set(['REGION', 'COUNTRY', 'AIRPORT']);

/**
 * Normalize a raw scope value (a token claim, a stored assignment, a request body) into
 * a `ScopeEntry[]`.
 *
 * | raw                                         | result                       |
 * |---------------------------------------------|------------------------------|
 * | `undefined` / `null` (absent)               | `null`                       |
 * | legacy single object `{kind, code?}`, valid | `[entry]`                    |
 * | array of valid entries                      | those entries (de-duplicated)|
 * | any valid shape containing `ALL`            | `[{ kind: 'ALL' }]`          |
 * | `[]`                                        | `[]` (no airports)           |
 * | anything malformed / unknown kind / a bad   | `[]` (no airports)           |
 * | element anywhere in an array                |                              |
 *
 * `null` is deliberately distinct from `[]`: absence keeps whatever meaning the caller's
 * contract already gives a scope-less principal (for the JWT claim: tenant-wide, as
 * today), while `[]` is an explicit "no airports". A partially-invalid array is `[]` as a
 * whole — a junk element must never be silently dropped while the rest still grants, and
 * an ALL next to junk must never read as unrestricted.
 *
 * Codes are trimmed and upper-cased (`expandScope` matches case-insensitively anyway);
 * `ALL` ignores any stray `code`. Never throws.
 */
export function normalizeScopeEntries(raw: unknown): ScopeEntry[] | null {
  if (raw === undefined || raw === null) return null;
  const candidates: readonly unknown[] = Array.isArray(raw) ? raw : [raw];

  const entries: ScopeEntry[] = [];
  const seen = new Set<string>();
  let all = false;
  for (const candidate of candidates) {
    const entry = toEntry(candidate);
    if (!entry) return []; // malformed anywhere ⇒ no airports at all
    if (entry.kind === 'ALL') {
      all = true;
      continue;
    }
    const id = `${entry.kind}:${entry.code}`;
    if (seen.has(id)) continue;
    seen.add(id);
    entries.push(entry);
  }
  return all ? [{ kind: 'ALL' }] : entries;
}

function toEntry(candidate: unknown): ScopeEntry | undefined {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return undefined;
  const { kind, code } = candidate as { kind?: unknown; code?: unknown };
  if (kind === 'ALL') return { kind: 'ALL' };
  if (typeof kind !== 'string' || !CODED_KINDS.has(kind)) return undefined;
  if (typeof code !== 'string') return undefined;
  const normalized = code.trim().toUpperCase();
  if (normalized.length === 0) return undefined;
  return { kind: kind as 'REGION' | 'COUNTRY' | 'AIRPORT', code: normalized };
}

/**
 * Fail-closed marker for the legacy single-object `scope` field.
 *
 * When a scope LIST cannot be represented as one `AuthScope` (several entries) or is
 * malformed / empty, the legacy field carries this instead of being left `undefined` —
 * every pre-list reader treats a missing scope as "unrestricted", so `undefined` would
 * fail open. It is an `AIRPORT` scope whose code can never be an IATA code, so every
 * existing `expandScope` caller resolves it to an EMPTY airport set (= sees nothing).
 * List-aware readers must ignore it and read the entry list (`scopes` / `airports`).
 */
export const SCOPE_NONE: Readonly<AuthScope> = Object.freeze({ kind: 'AIRPORT', code: '__NONE__' });

/** Is this the {@link SCOPE_NONE} marker (structurally — it survives JSON round-trips)? */
export function isScopeNone(scope: AuthScope | null | undefined): boolean {
  return !!scope && scope.kind === SCOPE_NONE.kind && scope.code === SCOPE_NONE.code;
}

/**
 * The legacy single-object view of a scope list, for producers that still populate the
 * v2 `scope` field (JWT context, `X-Operator-Context`):
 *
 *   - `null` / `undefined` (absent)  → `undefined` (scope-less: tenant-wide, as today)
 *   - contains `ALL`                 → `{ kind: 'ALL' }`
 *   - exactly one entry              → that entry
 *   - several entries, or `[]`       → {@link SCOPE_NONE} (fail closed for legacy readers)
 */
export function toLegacyScope(entries: readonly ScopeEntry[] | null | undefined): AuthScope | undefined {
  if (entries === null || entries === undefined) return undefined;
  if (entries.some((e) => e.kind === 'ALL')) return { kind: 'ALL' };
  const only = entries.length === 1 ? entries[0] : undefined;
  if (only && only.kind !== 'ALL') return { kind: only.kind, code: only.code };
  return { ...SCOPE_NONE };
}
